import React from 'react';
import { Box, Grid, Typography, Button, Chip, Container, Stack, Divider } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { SupervisedUserCircleSharp } from '@mui/icons-material';

// Placeholder stats - commented out until we have real numbers to show.
// const STATS = [
//   { value: '10K+', label: 'Slots booked' },
//   { value: '500+', label: 'Salon partners' },
//   { value: '4.5★', label: 'App rating' },
// ];

const HeroSection = () => {
  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 7, md: 12 },
        pb: { xs: 8, md: 12 },
        background:
          'radial-gradient(1200px 500px at 10% -10%, #dcedfa 0%, transparent 60%),' +
          'radial-gradient(900px 500px at 100% 0%, #e0f2fe 0%, transparent 55%),' +
          'linear-gradient(180deg, #ffffff 0%, #f5faff 100%)',
      }}
    >
      {/* Decorative blobs */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -120,
          right: -80,
          width: 380,
          height: 380,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(14,165,233,0.22), transparent 65%)',
          filter: 'blur(20px)',
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: -140,
          left: -100,
          width: 420,
          height: 420,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(32,133,216,0.18), transparent 65%)',
          filter: 'blur(20px)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          {/* Left side with text and buttons */}
          <Grid item xs={12} md={6}>
            <Chip
              icon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
              label="India's smart salon booking platform"
              sx={{
                mb: 2.5,
                px: 1,
                fontWeight: 600,
                color: '#135F97',
                backgroundColor: 'rgba(32,133,216,0.10)',
                border: '1px solid rgba(32,133,216,0.18)',
                '& .MuiChip-icon': { color: '#2085D8' },
              }}
            />

            <Typography
              variant="h2"
              component="h1"
              sx={{ fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' }, lineHeight: 1.1, mb: 2 }}
            >
              Book salon slots.{' '}
              <Box component="span" className="gradient-text">
                Skip the wait.
              </Box>
            </Typography>

            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', fontWeight: 400, maxWidth: 520, mb: 4 }}
            >
              Cizlot is the all-in-one platform for salon &amp; parlour booking and business
              management — connect with customers and grow your business effortlessly.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<SupervisedUserCircleSharp />}
                href="https://play.google.com/store/apps/details?id=com.cizlot.cizlot_user_app"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  py: 1.4,
                  px: 3,
                  borderColor: 'rgba(17,24,39,0.15)',
                  color: 'text.primary',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    borderColor: '#D57617',
                    backgroundColor: '#D57617',
                    color: '#fff',
                  },
                }}
              >
                Download User App
              </Button>

              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<StorefrontIcon />}
                href="https://play.google.com/store/apps/details?id=com.cizlot.cizlot_partner_app"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ py: 1.4, px: 3 }}
              >
                Partner App
              </Button>
            </Stack>

            {/* Stats row hidden until we have real numbers - we're just starting out.
            <Stack
              direction="row"
              spacing={{ xs: 2, sm: 4 }}
              divider={<Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(17,24,39,0.10)' }} />}
              sx={{ mt: 5 }}
            >
              {STATS.map((stat) => (
                <Box key={stat.label}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
            */}
          </Grid>

          {/* Right side visual */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                borderRadius: '28px',
                overflow: 'hidden',
                border: '1px solid rgba(17,24,39,0.06)',
                boxShadow: '0 30px 70px rgba(19,95,151,0.18)',
                transform: { md: 'rotate(-1.5deg)' },
              }}
            >
              <Box
                component="img"
                src="/img7-800.webp"
                srcSet="/img7-800.webp 800w, /img7-1200.webp 1200w"
                sizes="(max-width: 900px) 100vw, 560px"
                fetchPriority="high"
                decoding="async"
                alt="Cizlot salon booking app"
                sx={{ width: '100%', height: 'auto', display: 'block', aspectRatio: '4 / 3', objectFit: 'cover' }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* What is Cizlot */}
        <Box
          sx={{
            mt: { xs: 8, md: 12 },
            p: { xs: 3, md: 5 },
            borderRadius: '24px',
            backgroundColor: 'rgba(255,255,255,0.75)',
            border: '1px solid rgba(17,24,39,0.06)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Typography variant="h4" component="h2" sx={{ mb: 1.5 }}>
            What is Cizlot?
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 860, lineHeight: 1.8 }}>
            Cizlot is an online salon and parlour appointment booking app built for both customers
            and salon owners in India. Customers can easily book salon slots, avoid waiting time,
            and discover nearby salons. Salon owners can manage daily bookings, staff schedules,
            and customers from a single app.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSection;
